import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Globe, Save, ArrowLeft, Plus, Trash2, Image, Layout } from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";

const LandingPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [activeTab, setActiveTab] = useState("general");
  const [previewMode, setPreviewMode] = useState(false);

  const tabs = [
    { id: "general", label: "General" },
    { id: "hero", label: "Hero Section" },
    { id: "features", label: "Features" },
    { id: "testimonials", label: "Testimonials" },
    { id: "call-to-action", label: "Call to Action" },
    { id: "footer", label: "Footer" },
    { id: "custom-css", label: "Custom CSS" },
  ];

  const handleSave = () => {
    // Save landing page configuration
    alert("Landing page saved successfully!");
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/admin/marketing")}
            className="btn btn-circle btn-ghost"
            aria-label="Back to Marketing Hub"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Globe size={24} className="text-primary" />
              Landing Page Customization
            </h1>
            <p className="text-base-content/60">
              Customize your public-facing website to attract potential customers
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <div className="join">
            <button 
              className={`join-item btn ${!previewMode ? 'btn-active' : ''}`}
              onClick={() => setPreviewMode(false)}
            >
              <Layout size={16} />
              Edit
            </button>
            <button 
              className={`join-item btn ${previewMode ? 'btn-active' : ''}`}
              onClick={() => setPreviewMode(true)}
            >
              <Image size={16} />
              Preview
            </button>
          </div>
          <button onClick={handleSave} className="btn btn-primary">
            <Save size={16} />
            Save Changes
          </button>
        </div>
      </div>

      {previewMode ? (
        <div className="bg-base-200 rounded-box p-4 min-h-screen">
          <div className={`${
            isDarkMode
              ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5'
              : 'bg-base-100'
          } rounded-box shadow-xl p-4 text-center`}>
            <h2 className="text-lg font-semibold mb-4">Landing Page Preview</h2>
            <iframe 
              title="Landing Page Preview"
              className="w-full border border-base-300 rounded-box"
              style={{ height: "80vh" }}
              src="/preview/landing-page"
            >
              Your browser does not support iframes.
            </iframe>
          </div>
        </div>
      ) : (
        <>
          {/* Tabs */}
          <div className="tabs tabs-boxed bg-base-200 p-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab ${activeTab === tab.id ? "tab-active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className={`${
            isDarkMode
              ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5'
              : 'bg-base-100'
          } rounded-box shadow-sm p-6`}>
            {activeTab === "general" && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold mb-4">General Settings</h2>
                
                <div className="form-control w-full max-w-lg">
                  <label className="label">
                    <span className="label-text font-medium">Site Title</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Your Dance Competition"
                    className="input input-bordered w-full"
                    defaultValue="Dance Digital Competition"
                  />
                </div>
                
                <div className="form-control w-full max-w-lg">
                  <label className="label">
                    <span className="label-text font-medium">Tagline</span>
                  </label>
                  <input
                    type="text"
                    placeholder="A short description of your event"
                    className="input input-bordered w-full"
                    defaultValue="The premier dance competition for all ages"
                  />
                </div>
                
                <div className="form-control w-full max-w-lg">
                  <label className="label">
                    <span className="label-text font-medium">Primary Color</span>
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      className="input input-bordered h-10 w-20"
                      defaultValue="#3B82F6"
                    />
                    <input
                      type="text"
                      className="input input-bordered w-32"
                      defaultValue="#3B82F6"
                    />
                  </div>
                </div>
                
                <div className="form-control w-full max-w-lg">
                  <label className="label">
                    <span className="label-text font-medium">Logo</span>
                  </label>
                  <div className="flex items-center gap-4">
                    <div className="w-24 h-24 bg-base-200 rounded-md flex items-center justify-center border border-base-300">
                      <span className="text-base-content/30">Logo</span>
                    </div>
                    <button className="btn btn-outline">
                      <Image size={16} />
                      Upload Logo
                    </button>
                  </div>
                </div>
                
                <div className="form-control w-full max-w-lg">
                  <label className="label">
                    <span className="label-text font-medium">Favicon</span>
                  </label>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-base-200 rounded-md flex items-center justify-center border border-base-300">
                      <span className="text-base-content/30">Icon</span>
                    </div>
                    <button className="btn btn-outline">
                      <Image size={16} />
                      Upload Favicon
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "hero" && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold mb-4">Hero Section</h2>
                
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text font-medium">Hero Title</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Your main headline"
                    className="input input-bordered w-full max-w-lg"
                    defaultValue="Showcase Your Talent at Dance Digital"
                  />
                </div>
                
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text font-medium">Subtitle</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Supporting text"
                    className="input input-bordered w-full max-w-lg"
                    defaultValue="Join hundreds of dancers from across the country"
                  />
                </div>
                
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text font-medium">Background Image</span>
                  </label>
                  <div className="flex items-center gap-4">
                    <div className="w-40 h-24 bg-base-200 rounded-md flex items-center justify-center border border-base-300">
                      <span className="text-base-content/30">Background</span>
                    </div>
                    <button className="btn btn-outline">
                      <Image size={16} />
                      Upload Image
                    </button>
                  </div>
                </div>
                
                <div className="form-control w-full max-w-lg">
                  <label className="label">
                    <span className="label-text font-medium">Call to Action Button</span>
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Button text"
                      className="input input-bordered"
                      defaultValue="Register Now"
                    />
                    <input
                      type="text"
                      placeholder="Button URL"
                      className="input input-bordered"
                      defaultValue="/register"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === "features" && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold">Features Section</h2>
                  <button className="btn btn-outline btn-sm">
                    <Plus size={16} />
                    Add Feature
                  </button>
                </div>
                
                <div className="space-y-6">
                  <div className={`${
                    isDarkMode
                      ? 'bg-[#131620] backdrop-blur-sm border border-white/5'
                      : 'bg-base-200'
                  } p-4 rounded-xl`}>
                    <div className="flex justify-between mb-2">
                      <h3 className="font-medium">Feature 1</h3>
                      <button className="btn btn-ghost btn-circle btn-sm">
                        <Trash2 size={16} className="text-error" />
                      </button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">Title</span>
                        </label>
                        <input
                          type="text"
                          className="input input-bordered"
                          defaultValue="Professional Judging"
                        />
                      </div>
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">Icon</span>
                        </label>
                        <select className="select select-bordered">
                          <option>Trophy</option>
                          <option>Star</option>
                          <option>Award</option>
                          <option>Music</option>
                        </select>
                      </div>
                      <div className="form-control md:col-span-2">
                        <label className="label">
                          <span className="label-text">Description</span>
                        </label>
                        <textarea
                          className="textarea textarea-bordered h-20"
                          defaultValue="Our judges are industry professionals with decades of experience."
                        ></textarea>
                      </div>
                    </div>
                  </div>
                  
                  <div className={`${
                    isDarkMode
                      ? 'bg-[#131620] backdrop-blur-sm border border-white/5'
                      : 'bg-base-200'
                  } p-4 rounded-xl`}>
                    <div className="flex justify-between mb-2">
                      <h3 className="font-medium">Feature 2</h3>
                      <button className="btn btn-ghost btn-circle btn-sm">
                        <Trash2 size={16} className="text-error" />
                      </button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">Title</span>
                        </label>
                        <input
                          type="text"
                          className="input input-bordered"
                          defaultValue="Advanced Technology"
                        />
                      </div>
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">Icon</span>
                        </label>
                        <select className="select select-bordered">
                          <option>Computer</option>
                          <option>Star</option>
                          <option>Award</option>
                          <option>Music</option>
                        </select>
                      </div>
                      <div className="form-control md:col-span-2">
                        <label className="label">
                          <span className="label-text">Description</span>
                        </label>
                        <textarea
                          className="textarea textarea-bordered h-20"
                          defaultValue="Our digital scoring system ensures fair and accurate results."
                        ></textarea>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Other tab content would go here */}
            {activeTab !== "general" && activeTab !== "hero" && activeTab !== "features" && (
              <div className="py-8 text-center">
                <h3 className="text-lg font-medium text-base-content/60">
                  {tabs.find(tab => tab.id === activeTab)?.label} settings will appear here
                </h3>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default LandingPage; 